import { CheckIcon } from '@primer/octicons-react';
import { useRef } from 'react';
import { graphql, useFragment } from 'react-relay';

import { BookUpdateDialog_book$key } from '../../../relay/__generated__/BookUpdateDialog_book.graphql';
import { BookUpdateDialog_bookUpdateMutation } from '../../../relay/__generated__/BookUpdateDialog_bookUpdateMutation.graphql';
import Button from '../../core/Button';
import Dialog, { DialogProps } from '../../core/Dialog';
import EnumPairRadioGroupField from '../../core/EnumPairRadioGroupField';
import FormLayout from '../../core/FormLayout';
import Grid from '../../core/Grid';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import Text from '../../core/Text';
import TextField from '../../core/TextField';
import UploadField from '../../core/UploadField';
import UnitAPaginatorSearchOverlayTokenField from '../../unitA/UnitAPaginatorSearchOverlayTokenField';
import BookPublisherSearchOverlayField from '../BookPublisherSearchOverlayField';
import BookTitleSearchOverlayField from '../BookTitleSearchOverlayField';

const BookUpdateDialog_book = graphql`
  fragment BookUpdateDialog_book on Book {
    id
    type
    title
    unitAs {
      id
    }
    publisher
    edition
    coverPdf {
      key
      objectUrl
    }
    problemPdf {
      key
      objectUrl
    }
    solutionPdf {
      key
      objectUrl
    }
    bookScrapCount: bookScraps {
      totalCount
    }
  }
`;

type Props = { book: BookUpdateDialog_book$key } & DialogProps &
  Pick<MutationFormikProps<BookUpdateDialog_bookUpdateMutation>, 'onSubmit' | 'config' | 'connections'>;

const BookUpdateDialog = ({ book, onSubmit, config, connections, ...props }: Props) => {
  const { id, type, title, unitAs, publisher, edition, coverPdf, problemPdf, solutionPdf, bookScrapCount } =
    useFragment(BookUpdateDialog_book, book);

  // 저장 버튼을 누른 시점의 PDF 교체 결정. 폼 상태로 두면 "값을 바꾸고 바로 제출"이
  // 한 틱 안에서 엇갈릴 수 있어, 보내기 직전(valuesTransformer)에 확정해서 넣는다.
  const pdfReplaceDecisionRef = useRef<'rescrap' | 'keep-pdf' | null>(null);
  const originalPdfValues = {
    problemPdf: { ...problemPdf, size: 0 },
    solutionPdf: { ...solutionPdf, size: 0 },
  };

  return (
    <Dialog wide {...props}>
      <Dialog.Header>
        <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>PDF 정보 수정하기</Text>
      </Dialog.Header>
      <MutationFormik<BookUpdateDialog_bookUpdateMutation>
        mutation={graphql`
          mutation BookUpdateDialog_bookUpdateMutation($input: BookUpdateInput!) {
            bookUpdate(input: $input) {
              id
              ...BookUpdateDialog_book
            }
          }
        `}
        initialValues={{
          id,
          type,
          title,
          unitAs: unitAs.map(({ id }) => id),
          edition,
          publisher,
          coverPdf: { ...coverPdf, size: 0 },
          ...originalPdfValues,
        }}
        onSubmit={onSubmit}
        config={config}
        connections={connections}
        valuesTransformer={(values) => {
          if (pdfReplaceDecisionRef.current === 'rescrap') return { ...values, rescrap: true };
          // 아니오 = PDF 교체만 취소. 같은 화면에서 고친 교재 이름·출판사 등은 그대로 저장한다
          if (pdfReplaceDecisionRef.current === 'keep-pdf') return { ...values, ...originalPdfValues };
          return values;
        }}
      >
        {({ handleSubmit, values }, { scrollContainerRef, nonFieldErrorRef }) => {
          // 문제·해설 PDF 를 갈아끼우면 옛 크롭 이미지가 새 PDF 와 섞인다. 그래서 교체는
          // "기존 스크랩 정리 + 처음부터 다시 읽기"와 한 몸으로만 보낸다(서버도 같은 규칙).
          // 표지 PDF 는 스크랩과 무관해서 그냥 바뀐다.
          const isPdfReplaced =
            values.problemPdf?.key !== problemPdf.key || values.solutionPdf?.key !== solutionPdf.key;

          const handleSaveButtonClick = () => {
            if (!isPdfReplaced) {
              pdfReplaceDecisionRef.current = null;
            } else {
              const isRescrapConfirmed = window.confirm(
                `PDF가 바뀌었어요. 기존 문항 스크랩 ${
                  bookScrapCount.totalCount ?? 0
                }건을 정리하고 새 PDF로 다시 읽을까요?\n\n교재는 진행 중으로 돌아가고, 수학대왕에는 다시 제출할 때 한 번에 반영돼요.`,
              );
              pdfReplaceDecisionRef.current = isRescrapConfirmed ? 'rescrap' : 'keep-pdf';
            }
            handleSubmit();
          };

          return (
            <>
              <Dialog.Body ref={scrollContainerRef}>
                <FormLayout>
                  <NonFieldError ref={nonFieldErrorRef} />
                  <EnumPairRadioGroupField
                    typename={'BookTypeEnum'}
                    name={'type'}
                    label={'시중 교재/교과서'}
                    renderContainer={(children) => <Grid gapX={3}>{children}</Grid>}
                    renderOptionWrapper={(children, { id }) => (
                      <Grid.Unit key={id} size={'min'}>
                        {children}
                      </Grid.Unit>
                    )}
                    required
                  />
                  <Grid gapX={4}>
                    <Grid.Unit size={1 / 4}>
                      <BookTitleSearchOverlayField
                        label={'교재 이름'}
                        name={'title'}
                        placeholder={'e.g. 개념 해결의 법칙 - 초등수학 3-1'}
                        required
                      />
                    </Grid.Unit>
                    <Grid.Unit size={1 / 4}>
                      <UnitAPaginatorSearchOverlayTokenField
                        label={'Unit A'}
                        name={'unitAs'}
                        placeholder={'Unit A 선택'}
                        variables={{ order: { order: 'ASC' } }}
                        visibleTokenCount={1}
                        required
                      />
                    </Grid.Unit>
                    <Grid.Unit size={1 / 4}>
                      <BookPublisherSearchOverlayField
                        label={'출판사'}
                        name={'publisher'}
                        placeholder={'출판사 입력'}
                        required
                      />
                    </Grid.Unit>
                    <Grid.Unit size={1 / 4}>
                      <TextField label={'발행 연도'} name={'edition'} placeholder={'e.g. 2024'} required />
                    </Grid.Unit>
                  </Grid>
                  <UploadField label={'문제 표지 PDF'} name={'coverPdf'} accept={'application/pdf'} required />
                  <UploadField label={'문제 PDF'} name={'problemPdf'} accept={'application/pdf'} required />
                  <UploadField label={'해설 PDF'} name={'solutionPdf'} accept={'application/pdf'} required />
                </FormLayout>
              </Dialog.Body>
              <Dialog.Footer>
                <Button leadingIcon={CheckIcon} variant={'primary'} onClick={() => handleSaveButtonClick()}>
                  저장하기
                </Button>
              </Dialog.Footer>
            </>
          );
        }}
      </MutationFormik>
    </Dialog>
  );
};

export default BookUpdateDialog;
